/**
 * @fileoverview Picks the single target to show for a tenant from its target list.
 * @description One Tenant has at most one open Target at a time (see tenant.types.ts's Target doc
 * comment), but a tenant may also carry historical Completed targets — this is the one place that
 * decides which target a page should display: prefer the open (Outstanding) one; otherwise fall
 * back to the most recently created, so a tenant with only historical targets still shows its last
 * one rather than nothing. Used by both TenantDetailModal (one tenant) and any page joining a
 * target list across many tenants (e.g. Sales Pipeline).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { Target } from '@/features/sales/types/tenant.types'

/**
 * @description Picks the target to display from a tenant's target list.
 * @param {T[]} targets - The tenant's targets (typically 0-2 entries in this mock's data model).
 * @returns {T | undefined} The open target if one exists, otherwise the most recently created, or
 * undefined if the list is empty.
 */
export function pickRelevantTarget<T extends Pick<Target, 'status' | 'createdAt'>>(
  targets: T[],
): T | undefined {
  return (
    targets.find((candidate) => candidate.status === 'Outstanding') ??
    [...targets].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  )
}
